import { useMemo, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { ArrowLeft, Building2, Search } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { t } from '@/shared/lib/i18n'
import { sessionPath } from '@/shared/lib/routes'
import { cn } from '@/shared/lib/utils'
import { AuthFooter } from './AuthFooter'
import type { CustomerListItem } from '@/shared/types/CustomerListItem'

interface Props {
  customerList: CustomerListItem[]
}

const CustomerSelector = ({ customerList }: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const form = useForm({ customerId: '' })

  const filteredCustomers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return customerList
    }

    return customerList.filter((customer) =>
      customer.customerName.toLowerCase().includes(normalizedQuery),
    )
  }, [customerList, searchQuery])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ customer_id: data.customerId }))
    form.post(sessionPath())
  }

  const handleLogout = () => {
    form.delete(sessionPath())
  }

  return (
    <>
      {/* Nav bar — all breakpoints */}
      <div className="flex items-center border-b pb-4">
        <button
          type="button"
          onClick={handleLogout}
          className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
          aria-label={t('auth.customerSelector.cancel')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-center text-lg font-semibold">
          {t('auth.customerSelector.title')}
        </h1>
        <div className="h-9 w-9" aria-hidden />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col justify-between gap-5">
        {/* Top content: search + tiles */}
        <div className="flex flex-col gap-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="customer-search"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('auth.customerSelector.searchPlaceholder')}
              autoComplete="off"
              className="pl-9"
            />
          </div>

          {filteredCustomers.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('auth.customerSelector.empty')}</p>
          ) : (
            <div
              className="grid grid-cols-2 gap-3 pb-36 sm:grid-cols-2 sm:pb-0 lg:grid-cols-3"
              role="radiogroup"
              aria-label={t('auth.customerSelector.title')}
            >
              {filteredCustomers.map((customer) => {
                const customerId = String(customer.customerId)
                const isSelected = form.data.customerId === customerId

                return (
                  <button
                    key={customer.customerId}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => form.setData('customerId', customerId)}
                    className={cn(
                      'flex min-h-28 flex-col items-center justify-center gap-3 rounded-xl border px-4 py-5 text-center transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border bg-background hover:border-primary/40 hover:bg-muted/30',
                    )}
                  >
                    <Building2
                      className={cn(
                        'size-8',
                        isSelected ? 'text-primary' : 'text-muted-foreground',
                      )}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <span className="text-xs leading-snug font-medium">
                      {customer.customerName}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Fixed at bottom on mobile, natural bottom on sm+ via justify-between */}
        <div className="fixed bottom-0 left-0 right-0 border-t bg-background px-6 pb-8 pt-5 sm:relative sm:border-none sm:bg-transparent sm:p-0">
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={handleLogout}>
              {t('auth.customerSelector.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={form.processing || !form.data.customerId}
            >
              {t('auth.customerSelector.submit')}
            </Button>
          </div>
          <AuthFooter className="mt-5 sm:hidden" />
        </div>
      </form>
    </>
  )
}

export { CustomerSelector }
